import { Card, CardHeader, CardBody, Text, Flex, Box } from '@backstage/ui';
import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import { RELATION_HAS_PART, RELATION_DEPENDS_ON, Entity } from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import { RiServerLine } from '@remixicon/react';
import { TypeBadge } from '../badges/type-badge.component';
import { LifecycleBadge } from '../badges/lifecycle-badge.component';
import { EmptyState, Tile } from '../../../../shared/components';
import styles from './relations.module.css';

const HasResourcesContent = ({
  entities,
  loading,
  error,
}: {
  entities: Entity[] | undefined;
  loading: boolean;
  error: Error | undefined;
}) => {
  const { t } = useTranslationRef(appTranslationRef);

  if (loading) {
    return (
      <Flex direction="column" gap="3">
        {[1, 2, 3].map(i => (
          <Box key={i} className={styles.skeleton} style={{ height: '60px' }} />
        ))}
      </Flex>
    );
  }
  if (error) {
    return (
      <Text variant="body-medium" color="secondary">
        {t('hasResources.error')}
      </Text>
    );
  }
  if (!entities || entities.length === 0) {
    return <EmptyState title={t('hasResources.empty')} />;
  }
  return (
    <Flex direction="column" gap="3">
      {entities.map(resource => {
        const ref = `${resource.kind}:${resource.metadata.namespace || 'default'}/${resource.metadata.name}`;
        const type = (resource.spec?.type as string) || undefined;
        const lifecycle = (resource.spec?.lifecycle as string) || undefined;
        return (
          <Tile
            key={ref}
            href={`/catalog/${resource.metadata.namespace || 'default'}/${resource.kind.toLowerCase()}/${resource.metadata.name}`}
            title={resource.metadata.title || resource.metadata.name}
            description={resource.metadata.description}
            trailing={
              <>
                {type && <TypeBadge type={type} />}
                {lifecycle && <LifecycleBadge lifecycle={lifecycle} />}
              </>
            }
          />
        );
      })}
    </Flex>
  );
};

export const HasResourcesCard = () => {
  const { entity } = useEntity();
  const { t } = useTranslationRef(appTranslationRef);

  // System → Resource uses hasPart, Component → Resource uses dependsOn
  const relationType = entity.kind === 'System' ? RELATION_HAS_PART : RELATION_DEPENDS_ON;

  const { entities, loading, error } = useRelatedEntities(entity, {
    type: relationType,
    kind: 'resource',
  });

  return (
    <Card>
      <CardHeader>
        <Flex gap="3" align="center">
          <RiServerLine size={18} className={styles.headerIcon} />
          <Text variant="title-small" weight="bold">
            {t('hasResources.title')}
          </Text>
          {entities && entities.length > 0 && (
            <Text variant="body-x-small" className={styles.countBadge}>
              {entities.length}
            </Text>
          )}
        </Flex>
      </CardHeader>
      <CardBody>
        <HasResourcesContent entities={entities} loading={loading} error={error} />
      </CardBody>
    </Card>
  );
};
